import React, { useState, useEffect } from "react";
import axios from "axios";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";

const defaultTestimonials = [
  {
    id: 1,
    quote: "Beta Communes transformed our online presence with a stunning website and an effective digital marketing strategy. The team is professional, creative and truly understands business growth.",
    author: "Ahmed Raza",
    role: "Founder, Raza Builders",
    avatar: "/assets/ahmed_raza_avatar.jpg"
  },
  {
    id: 2,
    quote: "Working with Beta Communes has been an exceptional experience. Their custom software automated our operations and gave our team the clarity and efficiency we needed to scale.",
    author: "Hamza Malik",
    role: "Managing Director, Apex Logistics",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=300&q=80"
  },
  {
    id: 3,
    quote: "From day one, the Beta Communes team felt like an extension of our own. They delivered an intelligent AI workflow and seamless CRM that doubled our lead response rate.",
    author: "Sarah Jenkins",
    role: "Head of Growth, Nexus Retail",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80"
  }
];

const TestimonialSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [list, setList] = useState(defaultTestimonials);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const baseUrl = window.API_URL || "http://localhost:8000";
        const res = await axios.get(baseUrl + "/cms");
        if (res.data && Array.isArray(res.data.testimonials) && res.data.testimonials.length > 0) {
          const mapped = res.data.testimonials.map((item, idx) => ({
            id: item._id || item.id || idx,
            quote: item.review || item.text || item.quote,
            author: item.name || item.author,
            role: item.role ? `${item.role}${item.company ? `, ${item.company}` : ''}` : "Client Partner",
            avatar: item.avatar || (item.image ? (item.image.startsWith("http") ? item.image : `${baseUrl}/uploads/${item.image}`) : "/assets/ahmed_raza_avatar.jpg")
          }));
          // Put Ahmed Raza first if desired, or merge with CMS
          setList([defaultTestimonials[0], ...mapped]);
        }
      } catch (err) {
        // Fallback to default list
      }
    };
    fetchTestimonials();
  }, []);

  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? list.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex(prev => (prev === list.length - 1 ? 0 : prev + 1));
  };

  const current = list[currentIndex] || defaultTestimonials[0];

  return (
    <section className="py-14 sm:py-20 lg:py-24 bg-white text-slate-900 border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          
          {/* Left Column: Tag, Heading & Prev/Next Arrows */}
          <div className="lg:col-span-5 space-y-5 sm:space-y-6" data-aos="fade-right">
            
            {/* Tag */}
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-sm bg-[#f06600]"></span>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                WHAT OUR CLIENTS SAY
              </span>
            </div>

            {/* Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.18] sm:leading-[1.15]">
              Trusted by Businesses<br />
              That <span className="text-[#f06600]">Believe in Growth</span>
            </h2>

            {/* Carousel Navigation Arrows */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-full border border-slate-300 hover:border-[#f06600] text-slate-600 hover:text-[#f06600] flex items-center justify-center transition-all cursor-pointer shadow-sm hover:scale-105 active:scale-95"
                aria-label="Previous testimonial"
              >
                <BsChevronLeft className="text-sm" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full border border-slate-300 hover:border-[#f06600] text-slate-600 hover:text-[#f06600] flex items-center justify-center transition-all cursor-pointer shadow-sm hover:scale-105 active:scale-95"
                aria-label="Next testimonial"
              >
                <BsChevronRight className="text-sm" />
              </button>
              <span className="text-xs text-slate-400 font-medium ml-2 select-none">
                {currentIndex + 1} / {list.length}
              </span>
            </div>

          </div>

          {/* Right Column: Testimonial Card with Strict Dimension Stability */}
          <div className="lg:col-span-7" data-aos="fade-left">
            <div className="bg-[#fcfdfd] border border-slate-200/90 rounded-3xl p-6 sm:p-8 md:p-10 relative shadow-md hover:shadow-lg transition-all min-h-[280px] sm:min-h-[260px] flex flex-col justify-between">
              
              {/* Giant Quotation Marks */}
              <div className="text-slate-200 font-serif text-5xl leading-none absolute top-4 left-5 select-none pointer-events-none">
                “
              </div>
              <div className="text-slate-200 font-serif text-5xl leading-none absolute top-4 right-5 select-none pointer-events-none">
                ”
              </div>

              {/* Quote Body with Min Height to prevent height jumping */}
              <div className="relative z-10 px-1 sm:px-4 pt-4 pb-4 min-h-[110px] sm:min-h-[90px] flex items-center">
                <p className="text-slate-700 text-sm sm:text-base md:text-[16.5px] leading-relaxed font-normal">
                  "{current.quote}"
                </p>
              </div>

              {/* Author Info with strict locked image dimensions */}
              <div className="relative z-10 px-1 sm:px-4 pt-4 border-t border-slate-100 flex items-center gap-4">
                <div className="w-14 h-14 min-w-[56px] min-h-[56px] max-w-[56px] max-h-[56px] rounded-full overflow-hidden shrink-0 border-2 border-orange-200 shadow-sm bg-orange-50">
                  <img 
                    src={current.avatar} 
                    alt={current.author} 
                    className="w-full h-full object-cover object-center"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/assets/ahmed_raza_avatar.jpg";
                    }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-extrabold text-slate-900 text-base sm:text-lg leading-tight truncate">
                    {current.author}
                  </h4>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5 font-medium truncate">
                    {current.role}
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default TestimonialSection;
